/* eslint-disable */
<template>
  <div class="p-6 max-w-7xl mx-auto bg-white rounded-lg shadow-md">
    <!-- Header with Logo -->
    <div class="flex justify-between items-center mb-6 pb-4 border-b-2 border-blue-700">
      <div>
        <h2 class="text-3xl font-bold text-blue-900 uppercase">Individual Cover</h2>
        <p class="text-gray-600 mt-1">Individual Health Insurance Quote Calculator</p>
      </div>
      <div class="flex justify-end">
        <img :src="logo" alt="Logo" class="h-20 w-auto object-contain" />
      </div>
    </div>

    <!-- Premium Preview -->
    <div v-if="previewPremium > 0" class="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
      <div class="flex justify-between items-center">
        <div>
          <h3 class="font-semibold text-blue-900">Estimated Premium</h3>
          <p class="text-sm text-gray-600">For the current selection</p>
        </div>
        <div class="text-right">
          <p class="text-xl font-bold text-green-600">{{ formatAmount(previewPremium) }}</p>
          <p class="text-xs text-gray-500">per year</p>
        </div>
      </div>
    </div>

    <form @submit.prevent="addEntry" class="mb-8 p-6 bg-gray-50 rounded-lg shadow-md border-t-4 border-blue-700">
      <h3 class="text-xl font-semibold text-blue-900 mb-1">Add Member Details</h3>
      <p class="text-xs text-gray-500 mb-4">Individual Cover is priced per person — you can add as many Principals, Spouses and Children as you like.</p>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <!-- Member Name -->
        <div>
          <label for="memberName" class="block text-sm font-semibold text-blue-900 mb-1">Member Name</label>
          <input v-model="form.memberName" id="memberName" type="text" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required />
        </div>

        <!-- DOB -->
        <div>
          <label for="dob" class="block text-sm font-semibold text-blue-900 mb-1">Date of Birth</label>
          <input v-model="form.dob" id="dob" type="date" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required :max="maxDate" @change="calculatePreviewPremium"/>
        </div>

        <!-- Gender -->
        <div>
          <label for="gender" class="block text-sm font-semibold text-blue-900 mb-1">Gender</label>
          <select v-model="form.gender" id="gender" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required @change="calculatePreviewPremium">
            <option value="M">Male</option>
            <option value="F">Female</option>
          </select>
        </div>

        <!-- Relationship -->
        <div>
          <label for="relationship" class="block text-sm font-semibold text-blue-900 mb-1">Relationship</label>
          <select v-model="form.relationship" id="relationship" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required @change="calculatePreviewPremium">
            <option value="Principal">Principal</option>
            <option value="Spouse">Spouse</option>
            <option value="Child">Child</option>
          </select>
        </div>

        <!-- Benefits with Premium Badges -->
        <div>
          <label for="inpatientBenefit" class="block text-sm font-semibold text-blue-900 mb-1">
            Inpatient Benefit
            <span v-if="form.inpatientBenefit" class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded ml-2">
              +{{ formatAmount(calculatePreviewBenefit('inpatient', form.inpatientBenefit)) }}
            </span>
          </label>
          <select v-model="form.inpatientBenefit" id="inpatientBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" required @change="calculatePreviewPremium">
            <option v-for="amount in inpatientAmounts" :key="amount" :value="amount">{{ formatAmount(amount) }}</option>
          </select>
        </div>

        <div>
          <label for="outpatientBenefit" class="block text-sm font-semibold text-blue-900 mb-1">
            Outpatient Benefit
            <span v-if="form.outpatientBenefit" class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded ml-2">
              +{{ formatAmount(calculatePreviewBenefit('outpatient', form.outpatientBenefit)) }}
            </span>
          </label>
          <select v-model="form.outpatientBenefit" id="outpatientBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" @change="calculatePreviewPremium">
            <option v-for="amount in outpatientAmounts" :key="amount" :value="amount">{{ formatAmount(amount) }}</option>
          </select>
        </div>

        <div>
          <label for="dentalBenefit" class="block text-sm font-semibold text-blue-900 mb-1">
            Dental Benefit
            <span v-if="form.dentalBenefit" class="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded ml-2">
              +{{ formatAmount(calculatePreviewBenefit('dental', form.dentalBenefit)) }}
            </span>
          </label>
          <select v-model="form.dentalBenefit" id="dentalBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" :disabled="!form.outpatientBenefit" @change="calculatePreviewPremium">
            <option v-for="amount in dentalAmounts" :key="amount" :value="amount">{{ formatAmount(amount) }}</option>
          </select>
        </div>

        <div>
          <label for="opticalBenefit" class="block text-sm font-semibold text-blue-900 mb-1">
            Optical Benefit
            <span v-if="form.opticalBenefit" class="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded ml-2">
              +{{ formatAmount(calculatePreviewBenefit('optical', form.opticalBenefit)) }}
            </span>
          </label>
          <select v-model="form.opticalBenefit" id="opticalBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" :disabled="!form.outpatientBenefit" @change="calculatePreviewPremium">
            <option v-for="amount in opticalAmounts" :key="amount" :value="amount">{{ formatAmount(amount) }}</option>
          </select>
        </div>

        <div>
          <label for="maternityBenefit" class="block text-sm font-semibold text-blue-900 mb-1">
            Maternity Benefit
            <span v-if="form.maternityBenefit" class="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded ml-2">
              +{{ formatAmount(calculatePreviewBenefit('maternity', form.maternityBenefit)) }}
            </span>
          </label>
          <select v-model="form.maternityBenefit" id="maternityBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" :disabled="isMaternityDisabled" @change="calculatePreviewPremium">
            <option v-for="amount in maternityAmounts" :key="amount" :value="amount">{{ formatAmount(amount) }}</option>
          </select>
        </div>

        <!-- Femina Plus Benefit -->
        <div>
          <label for="feminaPlusBenefit" class="block text-sm font-semibold text-blue-900 mb-1">
            Femina Plus Benefit
            <span v-if="form.feminaPlusBenefit" class="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded ml-2">
              +{{ formatAmount(calculatePreviewFeminaPlus(form.feminaPlusBenefit)) }}
            </span>
          </label>
          <select v-model="form.feminaPlusBenefit" id="feminaPlusBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" :disabled="isMaternityDisabled" @change="calculatePreviewPremium">
            <option v-for="benefit in feminaPlusBenefits" :key="benefit" :value="benefit">{{ benefit || 'None' }}</option>
          </select>
        </div>
      </div>

      <!-- Submit Button -->
      <div class="flex items-end gap-2 mt-6">
        <button type="submit" class="flex-1 bg-blue-700 hover:bg-blue-800 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-300 transform hover:scale-105">
          <span v-if="previewPremium > 0">
            Add Member - {{ formatAmount(previewPremium) }}
          </span>
          <span v-else>
            Add Entry
          </span>
        </button>
      </div>
    </form>

    <!-- Loading & Discount Section -->
    <div class="mb-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg shadow-md border-t-4 border-purple-600">
      <h3 class="text-lg font-semibold text-purple-900 mb-4">Premium Adjustments</h3>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label class="block text-sm font-semibold text-purple-900 mb-1">Loading Percentage</label>
          <div class="flex items-center gap-2">
            <input v-model.number="loadingPercentage" type="number" step="0.1" min="0" max="100"
              @input="updateLoadingDiscount"
              class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition">
            <span class="text-gray-600 font-semibold">%</span>
          </div>
          <p class="text-xs text-gray-500 mt-1">Increase premium by percentage (e.g., for high-risk cases)</p>
        </div>
        <div>
          <label class="block text-sm font-semibold text-purple-900 mb-1">Discount Percentage</label>
          <div class="flex items-center gap-2">
            <input v-model.number="discountPercentage" type="number" step="0.1" min="0" max="100"
              @input="updateLoadingDiscount"
              class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition">
            <span class="text-gray-600 font-semibold">%</span>
          </div>
          <p class="text-xs text-gray-500 mt-1">Reduce premium by percentage (e.g., for loyalty discounts)</p>
        </div>
      </div>
      <div class="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
        <p class="text-sm text-yellow-800">
          <span class="font-semibold">Note:</span> Loading and discount are applied to the total premium.
          Net effect = Basic Premium × (1 + Loading% - Discount%)
        </p>
      </div>
    </div>
  </div>

  <!---begin of pdf report -->
  <div class="p-6 max-w-7xl mx-auto bg-white rounded-lg shadow-md mt-6">
    <div class="flex justify-between items-center mb-6 pb-4 border-b-2 border-blue-700">
      <div>
        <h3 class="text-lg font-semibold text-blue-900">Client Details</h3>
        <p class="text-xs text-gray-500">Shown on the generated quote PDF</p>
      </div>
      <img :src="logo" alt="Logo" class="h-16 w-auto object-contain" />
    </div>
    <div class="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4">
      <div>
        <p class="text-xs font-semibold text-blue-900 uppercase">Name</p>
        <p class="text-sm text-gray-700">{{ this.clientDetails.name || '-' }}</p>
      </div>
      <div>
        <p class="text-xs font-semibold text-blue-900 uppercase">Email</p>
        <p class="text-sm text-gray-700">{{ this.clientDetails.email || '-' }}</p>
      </div>
      <div>
        <p class="text-xs font-semibold text-blue-900 uppercase">Mobile</p>
        <p class="text-sm text-gray-700">{{ this.clientDetails.phone || '-' }}</p>
      </div>
      <div>
        <p class="text-xs font-semibold text-blue-900 uppercase">Intermediary</p>
        <p class="text-sm text-gray-700">{{ this.clientDetails.intermediary || '-' }}</p>
      </div>
    </div>

    <!-- Results Table -->
    <div v-if="entries.length" class="mt-6">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-xl font-bold text-blue-900">Members List ({{ entries.length }} member{{ entries.length !== 1 ? 's' : '' }})</h3>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full border-collapse border border-blue-300 hidden md:table">
          <thead>
            <tr class="bg-blue-700 text-white">
              <th class="border border-blue-300 p-2">Member Name</th>
              <th class="border border-blue-300 p-2">DOB</th>
              <th class="border border-blue-300 p-2">Age</th>
              <th class="border border-blue-300 p-2">Gender</th>
              <th class="border border-blue-300 p-2">Relationship</th>
              <th class="border border-blue-300 p-2">Inpatient Limit</th>
              <th class="border border-blue-300 p-2">Inpatient Premium</th>
              <th class="border border-blue-300 p-2">Outpatient Limit</th>
              <th class="border border-blue-300 p-2">Outpatient Premium</th>
              <th class="border border-blue-300 p-2">Dental Limit</th>
              <th class="border border-blue-300 p-2">Dental Premium</th>
              <th class="border border-blue-300 p-2">Optical Limit</th>
              <th class="border border-blue-300 p-2">Optical Premium</th>
              <th class="border border-blue-300 p-2">Maternity Limit</th>
              <th class="border border-blue-300 p-2">Maternity Premium</th>
              <th class="border border-blue-300 p-2">Femina Plus</th>
              <th class="border border-blue-300 p-2">Femina Premium</th>
              <th class="border border-blue-300 p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.entry_id" class="hover:bg-blue-50 transition">
              <td class="border border-blue-300 p-2">{{ entry.memberName }}</td>
              <td class="border border-blue-300 p-2">{{ entry.dob }}</td>
              <td class="border border-blue-300 p-2 text-center">{{ entry.age }}</td>
              <td class="border border-blue-300 p-2 text-center">{{ entry.gender }}</td>
              <td class="border border-blue-300 p-2">{{ entry.relationship }}</td>
              <td class="border border-blue-300 p-2">{{ formatAmount(entry.inpatientBenefit) }}</td>
              <td class="border border-blue-300 p-2 bg-gray-50">{{ formatAmount(calculatePremium('inpatient', entry.inpatientBenefit, entry.relationship, entry.age, entry.gender, entry)) }}</td>
              <td class="border border-blue-300 p-2">{{ formatAmount(entry.outpatientBenefit) }}</td>
              <td class="border border-blue-300 p-2 bg-gray-50">{{ formatAmount(calculatePremium('outpatient', entry.outpatientBenefit, entry.relationship, entry.age, entry.gender, entry)) }}</td>
              <td class="border border-blue-300 p-2">{{ formatAmount(entry.dentalBenefit) }}</td>
              <td class="border border-blue-300 p-2 bg-gray-50">{{ formatAmount(calculatePremium('dental', entry.dentalBenefit, entry.relationship, entry.age, entry.gender, entry)) }}</td>
              <td class="border border-blue-300 p-2">{{ formatAmount(entry.opticalBenefit) }}</td>
              <td class="border border-blue-300 p-2 bg-gray-50">{{ formatAmount(calculatePremium('optical', entry.opticalBenefit, entry.relationship, entry.age, entry.gender, entry)) }}</td>
              <td class="border border-blue-300 p-2">{{ formatAmount(entry.maternityBenefit) }}</td>
              <td class="border border-blue-300 p-2 bg-gray-50">{{ formatAmount(calculatePremium('maternity', entry.maternityBenefit, entry.relationship, entry.age, entry.gender, entry)) }}</td>
              <td class="border border-blue-300 p-2">{{ entry.feminaPlusBenefit || '-' }}</td>
              <td class="border border-blue-300 p-2 bg-gray-50">{{ formatAmount(calculateFeminaPlusPremium(entry.feminaPlusBenefit)) }}</td>
              <td class="border border-blue-300 p-2">
                <div class="flex space-x-2 justify-center">
                  <button
                    @click="editEntry(entry)"
                    class="text-blue-600 hover:text-blue-800"
                    title="Edit Entry"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/>
                    </svg>
                  </button>
                  <button
                    @click="deleteEntry(entry.entry_id)"
                    :disabled="isDeleteDisabled(entry.relationship)"
                    class="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Mobile Table -->
        <div class="md:hidden space-y-4">
          <div v-for="entry in entries" :key="entry.entry_id" class="border border-blue-200 p-4 rounded-lg shadow-sm">
            <div class="grid grid-cols-2 gap-2 text-sm">
              <div class="font-semibold text-blue-900">Member Name:</div>
              <div>{{ entry.memberName }}</div>
              <div class="font-semibold text-blue-900">DOB:</div>
              <div>{{ entry.dob }}</div>
              <div class="font-semibold text-blue-900">Age:</div>
              <div>{{ entry.age }}</div>
              <div class="font-semibold text-blue-900">Gender:</div>
              <div>{{ entry.gender }}</div>
              <div class="font-semibold text-blue-900">Relationship:</div>
              <div>{{ entry.relationship }}</div>
              <div class="font-semibold text-blue-900">Inpatient:</div>
              <div>{{ formatAmount(entry.inpatientBenefit) }} ({{ formatAmount(calculatePremium('inpatient', entry.inpatientBenefit, entry.relationship, entry.age, entry.gender, entry)) }})</div>
              <div class="font-semibold text-blue-900">Outpatient:</div>
              <div>{{ formatAmount(entry.outpatientBenefit) }} ({{ formatAmount(calculatePremium('outpatient', entry.outpatientBenefit, entry.relationship, entry.age, entry.gender, entry)) }})</div>
              <div class="font-semibold text-blue-900">Dental:</div>
              <div>{{ formatAmount(entry.dentalBenefit) }} ({{ formatAmount(calculatePremium('dental', entry.dentalBenefit, entry.relationship, entry.age, entry.gender, entry)) }})</div>
              <div class="font-semibold text-blue-900">Optical:</div>
              <div>{{ formatAmount(entry.opticalBenefit) }} ({{ formatAmount(calculatePremium('optical', entry.opticalBenefit, entry.relationship, entry.age, entry.gender, entry)) }})</div>
              <div class="font-semibold text-blue-900">Maternity:</div>
              <div>{{ formatAmount(entry.maternityBenefit) }} ({{ formatAmount(calculatePremium('maternity', entry.maternityBenefit, entry.relationship, entry.age, entry.gender, entry)) }})</div>
              <div class="font-semibold text-blue-900">Femina Plus:</div>
              <div>{{ entry.feminaPlusBenefit || '-' }} ({{ formatAmount(calculateFeminaPlusPremium(entry.feminaPlusBenefit)) }})</div>
            </div>
            <div class="mt-4 flex justify-end space-x-4">
              <button
                @click="editEntry(entry)"
                class="text-blue-600 hover:text-blue-800 font-semibold text-sm"
              >
                Edit
              </button>
              <button
                @click="deleteEntry(entry.entry_id)"
                :disabled="isDeleteDisabled(entry.relationship)"
                class="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Premium Summary Cards -->
      <div class="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-4 text-white">
          <p class="text-sm opacity-90">Basic Premium</p>
          <p class="text-2xl font-bold">{{ formatAmount(basicPremium) }}</p>
        </div>
        <div class="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-4 text-white" v-if="loadingPercentage > 0">
          <p class="text-sm opacity-90">Loading ({{ loadingPercentage }}%)</p>
          <p class="text-2xl font-bold">{{ formatAmount(loadingAmount) }}</p>
        </div>
        <div class="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-4 text-white" v-if="discountPercentage > 0">
          <p class="text-sm opacity-90">Discount ({{ discountPercentage }}%)</p>
          <p class="text-2xl font-bold">{{ formatAmount(discountAmount) }}</p>
        </div>
        <div class="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg shadow-lg p-4 text-white">
          <p class="text-sm opacity-90">Levies @ 0.45%</p>
          <p class="text-2xl font-bold">{{ formatAmount(levies) }}</p>
        </div>
        <div class="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-4 text-white">
          <p class="text-sm opacity-90">Stamp Duty</p>
          <p class="text-2xl font-bold">{{ formatAmount(stampDuty) }}</p>
        </div>
        <div class="bg-gradient-to-r from-red-600 to-red-700 rounded-lg shadow-lg p-4 text-white md:col-span-5">
          <p class="text-sm opacity-90">Total Premium (after adjustments)</p>
          <p class="text-3xl font-bold">{{ formatAmount(adjustedTotalPremium) }}</p>
          <p class="text-xs opacity-75 mt-1" v-if="loadingPercentage > 0 || discountPercentage > 0">
            Applied: {{ loadingPercentage > 0 ? `+${loadingPercentage}%` : '' }} {{ discountPercentage > 0 ? `-${discountPercentage}%` : '' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-12">
      <div class="text-6xl mb-4">📋</div>
      <h3 class="text-xl font-semibold text-gray-600 mb-2">No Members Added Yet</h3>
      <p class="text-gray-500">Please add members above to generate an insurance quote</p>
    </div>
  </div>
  <!--End of pdf report -->

  <!-- Edit Modal -->
  <div v-if="editingEntry" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-90vh overflow-y-auto">
      <div class="p-4 border-b-2 border-blue-700">
        <h3 class="text-lg font-semibold text-blue-900">Edit Member: {{ editingEntry.memberName }}</h3>
      </div>

      <div class="p-4">
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label class="block text-sm font-semibold text-blue-900 mb-1">Member Name</label>
            <input v-model="editingEntry.memberName" type="text" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
          </div>

          <div>
            <label class="block text-sm font-semibold text-blue-900 mb-1">Date of Birth</label>
            <input v-model="editingEntry.dob" type="date" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" :max="maxDate">
          </div>

          <div>
            <label class="block text-sm font-semibold text-blue-900 mb-1">Inpatient Benefit</label>
            <select v-model="editingEntry.inpatientBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
              <option v-for="amount in inpatientAmounts" :key="amount" :value="amount">
                {{ formatAmount(amount) }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-semibold text-blue-900 mb-1">Outpatient Benefit</label>
            <select v-model="editingEntry.outpatientBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
              <option v-for="amount in outpatientAmounts" :key="amount" :value="amount">
                {{ formatAmount(amount) }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-semibold text-blue-900 mb-1">Dental Benefit</label>
            <select v-model="editingEntry.dentalBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
              <option v-for="amount in dentalAmounts" :key="amount" :value="amount">
                {{ formatAmount(amount) }}
              </option>
            </select>
          </div>

          <div>
            <label class="block text-sm font-semibold text-blue-900 mb-1">Optical Benefit</label>
            <select v-model="editingEntry.opticalBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
              <option v-for="amount in opticalAmounts" :key="amount" :value="amount">
                {{ formatAmount(amount) }}
              </option>
            </select>
          </div>

          <div v-if="editingEntry.gender === 'F' && editingEntry.relationship !== 'Child'">
            <label class="block text-sm font-semibold text-blue-900 mb-1">Maternity Benefit</label>
            <select v-model="editingEntry.maternityBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
              <option v-for="amount in maternityAmounts" :key="amount" :value="amount">
                {{ formatAmount(amount) }}
              </option>
            </select>
          </div>

          <div v-if="editingEntry.gender === 'F' && editingEntry.relationship !== 'Child'">
            <label class="block text-sm font-semibold text-blue-900 mb-1">Femina Plus Benefit</label>
            <select v-model="editingEntry.feminaPlusBenefit" class="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition">
              <option v-for="benefit in feminaPlusBenefits" :key="benefit" :value="benefit">
                {{ benefit || 'None' }}
              </option>
            </select>
          </div>
        </div>

        <div class="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div class="flex justify-between items-center">
            <span class="font-semibold text-blue-900">Updated Premium:</span>
            <span class="font-bold text-green-600">
              {{ formatAmount(calculateTotalPremium(editingEntry)) }}
            </span>
          </div>
        </div>
      </div>

      <div class="p-4 border-t flex justify-end space-x-2">
        <button @click="cancelEdit" class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-semibold transition">
          Cancel
        </button>
        <button @click="saveEdit" class="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 font-semibold transition">
          Save Changes
        </button>
      </div>
    </div>
  </div>

  <div v-if="entries.length" class="p-6 max-w-7xl mx-auto bg-white rounded-lg shadow-md mt-6 flex justify-center space-x-4">
    <button @click="clearEntries" type="button" class="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 transform hover:scale-105">
      Clear Entries
    </button>
    <button @click="printQuote" type="button" class="bg-blue-700 hover:bg-blue-800 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 transform hover:scale-105">
      Generate Quote
    </button>
    <button @click="backHome" type="button" class="bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transition duration-300 transform hover:scale-105">
      Back Home
    </button>
  </div>
</template>

<script>
import benefits from '@/data/perperson.json';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  drawHeaderBanner, drawClientInfo, drawSectionBar, ensureSpace,
  drawBulletList, addPageNumber, drawFooterBlock, drawPremiumSummaryBoxes,
  generateReference, THEME,
} from '@/utils/pdfLayout';

const BENEFIT_KEY_MAP = {
  inpatient: 'inpatientBenefit',
  outpatient: 'outpatientBenefit',
  dental: 'dentalBenefit',
  optical: 'opticalBenefit',
  maternity: 'maternityBenefit',
};

// Family differentiating benefits
const FAMILY_BENEFITS = [
  'inpatient',
  'outpatient',
  'dental',
  'optical'
];

export default {
  data() {
    return {
      form: {
        memberName: '',
        dob: '',
        gender: 'M',
        relationship: 'Principal',
        inpatientBenefit: 500000,
        outpatientBenefit: null,
        dentalBenefit: null,
        opticalBenefit: null,
        maternityBenefit: null,
        feminaPlusBenefit: null
      },
      entries: JSON.parse(localStorage.getItem('entries')) || [],
      benefits,
      logo: require('@/assets/logo.png'),
      clientDetails: JSON.parse(localStorage.getItem('clientDetails')) || {
        name: this.$route.query.name || '',
        email: this.$route.query.email || '',
        phone: this.$route.query.phone || '',
        intermediary: this.$route.query.intermediary || ''
      },
      editingEntry: null,
      previewPremium: 0,
      loadingPercentage: 0,
      discountPercentage: 0
    };
  },

  mounted() {
    this.syncBenefits();

    // normalize old entries
    this.entries = this.entries.map(e => ({
      ...e,
      gender: e.gender || 'M'
    }));

    localStorage.setItem('entries', JSON.stringify(this.entries));

    // Load saved loading/discount values
    const savedLoading = localStorage.getItem('individual_loading_percentage');
    const savedDiscount = localStorage.getItem('individual_discount_percentage');
    if (savedLoading) this.loadingPercentage = parseFloat(savedLoading);
    if (savedDiscount) this.discountPercentage = parseFloat(savedDiscount);
  },
  computed: {
    inpatientAmounts() {
      return [500000, 1000000, 2000000, 3000000, 5000000, 7500000, 10000000];
    },
    outpatientAmounts() {
      return [null, 50000, 75000, 100000, 150000, 200000];
    },
    dentalAmounts() {
      return [null, 10000, 20000, 30000, 40000];
    },
    opticalAmounts() {
      return [null, 10000, 20000, 30000, 40000];
    },
    maternityAmounts() {
      return [null, 50000, 100000, 150000, 200000, 250000];
    },
    feminaPlusBenefits() {
      return [null, 'Silver', 'Gold', 'Platinum'];
    },
    isMaternityDisabled() {
      return this.form.gender === 'M' || this.form.relationship === 'Child';
    },
    basicPremium() {
      return this.entries.reduce((total, entry) => {
        return total + this.calculateTotalPremium(entry);
      }, 0);
    },
    loadingAmount() {
      return this.basicPremium * (this.loadingPercentage / 100);
    },
    discountAmount() {
      return this.basicPremium * (this.discountPercentage / 100);
    },
    adjustedBasicPremium() {
      return this.basicPremium + this.loadingAmount - this.discountAmount;
    },
    levies() {
      return this.adjustedBasicPremium * 0.0045;
    },
    stampDuty() {
      return this.entries.length * 40;
    },
    adjustedTotalPremium() {
      return this.adjustedBasicPremium + this.levies + this.stampDuty;
    },
    totalPremium() {
      return this.basicPremium + this.levies + this.stampDuty;
    },
    maxDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = ('0' + (today.getMonth() + 1)).slice(-2);
      const day = ('0' + today.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    }
  },

  methods: {
    updateLoadingDiscount() {
      if (this.loadingPercentage < 0) this.loadingPercentage = 0;
      if (this.loadingPercentage > 100) this.loadingPercentage = 100;
      if (this.discountPercentage < 0) this.discountPercentage = 0;
      if (this.discountPercentage > 100) this.discountPercentage = 100;

      localStorage.setItem('individual_loading_percentage', this.loadingPercentage);
      localStorage.setItem('individual_discount_percentage', this.discountPercentage);
    },

    formatAmount(amount) {
      if (amount === null || amount === undefined) {
        return '-';
      }
      return amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },

    getBenefitValue(entry, type) {
      const key = BENEFIT_KEY_MAP[type];
      if (!key) return undefined;

      const value = entry[key];
      if (value === null || value === '' || value === '-' || value === 0 || value === '0') {
        return undefined;
      }

      return Number(value);
    },

    childBenefitsMatchPrincipal(childEntry) {
      const entries = JSON.parse(localStorage.getItem('entries')) || [];

      // find all principals
      const principals = entries.filter(
        member => member.relationship === 'Principal'
      );

      if (!principals.length) return false;

      // check if child matches ANY principal
      return principals.some(principal =>
        FAMILY_BENEFITS.every(type =>
          this.getBenefitValue(principal, type) ===
          this.getBenefitValue(childEntry, type)
        )
      );
    },

    calculatePreviewBenefit(type, limit) {
      if (!limit) return 0;

      const age = this.form.dob
        ? this.calculateAge(this.form.dob)
        : 30;

      const relationship = this.form.relationship;
      // const gender = this.form.gender;

      if (type === 'dental' || type === 'optical' || type === 'maternity') {
        return this.benefits[type][limit] || 0;
      }

      const getAgeRangeKey = (age) => {
        if (age <= 20) return "20";
        if (age <= 40) return "40";
        if (age <= 54) return "54";
        if (age <= 65) return "65";
        if (age <= 70) return "70";
        if (age <= 75) return "75";
        if (age <= 80) return "80";
        return "40";
      };

      /* ================= OUTPATIENT ================= */
      if (type === 'outpatient') {
        const ageRangeKey = getAgeRangeKey(age);

        // Principal under 21 → child-only rate
        if (relationship === 'Principal' && age < 21) {
          return this.benefits['outpatient_child'][limit] || 0;
        }

        // Child under 21
        if (relationship === 'Child' && age < 21) {
          const matchesPrincipal = this.childBenefitsMatchPrincipal(this.form);

          if (!matchesPrincipal) {
            return this.benefits['outpatient_child'][limit] || 0;
          }

          return this.benefits['outpatient']["20"]?.[limit] || 0;
        }

        return this.benefits['outpatient'][ageRangeKey]?.[limit] || 0;
      }

      /* ================= INPATIENT ================= */
      if (type === 'inpatient') {
        const ageRangeKey = getAgeRangeKey(age);
        let premium = 0;

        // Principal under 21 → child-only rate
        if (relationship === 'Principal' && age < 21) {
          premium = this.benefits['child'][limit] || 0;
        }
        // Child under 21
        else if (relationship === 'Child' && age < 21) {
          const matchesPrincipal = this.childBenefitsMatchPrincipal(this.form);

          if (!matchesPrincipal) {
            premium = this.benefits['child'][limit] || 0;
          } else {
            premium = this.benefits['principal']["20"]?.[limit] || 0;
          }
        }
        // Normal rating
        else {
          premium = this.benefits['principal'][ageRangeKey]?.[limit] || 0;
        }

        return premium;
      }

      return 0;
    },

    calculatePreviewFeminaPlus(benefit) {
      if (!benefit) return 0;
      return this.benefits['feminaPlus'][benefit] || 0;
    },

    calculatePreviewPremium() {
      if (!this.form.dob) {
        this.previewPremium = 0;
        return;
      }

      let total = 0;
      total += this.calculatePreviewBenefit('inpatient', this.form.inpatientBenefit);
      total += this.calculatePreviewBenefit('outpatient', this.form.outpatientBenefit);
      total += this.calculatePreviewBenefit('dental', this.form.dentalBenefit);
      total += this.calculatePreviewBenefit('optical', this.form.opticalBenefit);

      if (this.form.gender === 'F' && this.form.relationship !== 'Child') {
        total += this.calculatePreviewBenefit('maternity', this.form.maternityBenefit);
        total += this.calculatePreviewFeminaPlus(this.form.feminaPlusBenefit);
      }

      this.previewPremium = total;
    },

    calculatePremium(type, limit, relationship, age, gender, entry = null) {
      if (!limit) return 0;

      if (type === 'dental' || type === 'optical' || type === 'maternity') {
        return this.benefits[type][limit] || 0;
      }

      const getAgeRangeKey = (age) => {
        if (age <= 20) return "20";
        if (age > 20 && age <= 40) return "40";
        if (age > 40 && age <= 54) return "54";
        if (age > 54 && age <= 65) return "65";
        if (age > 65 && age <= 70) return "70";
        if (age > 70 && age <= 75) return "75";
        if (age > 75 && age <= 80) return "80";
        return "40";
      };

      if (type === 'outpatient') {
        const ageRangeKey = getAgeRangeKey(age);

        // Principal under 21 → child-only rate
        if (relationship === 'Principal' && age < 21) {
          return this.benefits['outpatient_child'][limit] || 0;
        }

        // Child under 21
        if (relationship === 'Child' && age < 21) {
          const matchesPrincipal = entry
            ? this.childBenefitsMatchPrincipal(entry)
            : false;

          if (!matchesPrincipal) {
            return this.benefits['outpatient_child'][limit] || 0;
          }

          return this.benefits['outpatient']["20"]?.[limit] || 0;
        }

        return this.benefits['outpatient'][ageRangeKey]?.[limit] || 0;
      }

      if (type === 'inpatient') {
        const ageRangeKey = getAgeRangeKey(age);
        let premium = 0;

        // Principal under 21 → child-only rate
        if (relationship === 'Principal' && age < 21) {
          premium = this.benefits['child'][limit] || 0;
        }
        // Child under 21
        else if (relationship === 'Child' && age < 21) {
          const matchesPrincipal = entry
            ? this.childBenefitsMatchPrincipal(entry)
            : false;

          if (!matchesPrincipal) {
            premium = this.benefits['child'][limit] || 0;
          } else {
            premium = this.benefits['principal']["20"]?.[limit] || 0;
          }
        }
        // Normal rating
        else {
          premium = this.benefits['principal'][ageRangeKey]?.[limit] || 0;
        }

        return premium;
      }

      return 0;
    },

    calculateFeminaPlusPremium(benefit) {
      if (!benefit) return 0;
      return this.benefits['feminaPlus'][benefit] || 0;
    },

    calculateTotalPremium(entry) {
      let total = 0;
      if (entry.inpatientBenefit) {
        total += this.calculatePremium('inpatient', entry.inpatientBenefit, entry.relationship, entry.age, entry.gender, entry);
      }
      if (entry.outpatientBenefit) {
        total += this.calculatePremium('outpatient', entry.outpatientBenefit, entry.relationship, entry.age, entry.gender, entry);
      }
      if (entry.dentalBenefit) {
        total += this.calculatePremium('dental', entry.dentalBenefit, entry.relationship, entry.age, entry.gender, entry);
      }
      if (entry.opticalBenefit) {
        total += this.calculatePremium('optical', entry.opticalBenefit, entry.relationship, entry.age, entry.gender, entry);
      }
      if (entry.maternityBenefit) {
        total += this.calculatePremium('maternity', entry.maternityBenefit, entry.relationship, entry.age, entry.gender, entry);
      }
      if (entry.feminaPlusBenefit) {
        total += this.calculateFeminaPlusPremium(entry.feminaPlusBenefit);
      }
      return total;
    },

    calculateAge(dob) {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDifference = today.getMonth() - birthDate.getMonth();
      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },

    addEntry() {
      const age = this.calculateAge(this.form.dob);
      const entry_id = Date.now();
      const newEntry = { ...this.form, age, entry_id };

      if (this.entries.length === 0) {
        localStorage.setItem('firstEntryBenefits', JSON.stringify({
          inpatientBenefit: this.form.inpatientBenefit,
          outpatientBenefit: this.form.outpatientBenefit,
          dentalBenefit: this.form.dentalBenefit,
          opticalBenefit: this.form.opticalBenefit
        }));
      }

      this.entries.push(newEntry);
      localStorage.setItem('entries', JSON.stringify(this.entries));
      this.syncBenefits();
      this.resetForm();
      this.previewPremium = 0;
    },

    editEntry(entry) {
      this.editingEntry = { ...entry };
    },

    saveEdit() {
      if (this.editingEntry) {
        if (this.editingEntry.dob) {
          this.editingEntry.age = this.calculateAge(this.editingEntry.dob);
        }

        const index = this.entries.findIndex(entry => entry.entry_id === this.editingEntry.entry_id);
        if (index !== -1) {
          this.entries[index] = { ...this.editingEntry };
          localStorage.setItem('entries', JSON.stringify(this.entries));
        }

        this.cancelEdit();
      }
    },

    cancelEdit() {
      this.editingEntry = null;
    },

    deleteEntry(id) {
      if (confirm('Are you sure you want to delete this member?')) {
        this.entries = this.entries.filter(entry => entry.entry_id !== id);
        localStorage.setItem('entries', JSON.stringify(this.entries));
      }
    },

    isDeleteDisabled(relationship) {
      return relationship === 'Principal';
    },

    syncBenefits() {
      const firstEntryBenefits = JSON.parse(localStorage.getItem('firstEntryBenefits'));
      if (firstEntryBenefits) {
        this.form.inpatientBenefit = firstEntryBenefits.inpatientBenefit;
        this.form.outpatientBenefit = firstEntryBenefits.outpatientBenefit;
        this.form.dentalBenefit = firstEntryBenefits.dentalBenefit;
        this.form.opticalBenefit = firstEntryBenefits.opticalBenefit;
      }
    },

    resetForm() {
      this.form = {
        memberName: '',
        dob: '',
        gender: 'M',
        relationship: 'Principal',
        inpatientBenefit: JSON.parse(localStorage.getItem('firstEntryBenefits'))?.inpatientBenefit || 500000,
        outpatientBenefit: JSON.parse(localStorage.getItem('firstEntryBenefits'))?.outpatientBenefit || null,
        dentalBenefit: JSON.parse(localStorage.getItem('firstEntryBenefits'))?.dentalBenefit || null,
        opticalBenefit: JSON.parse(localStorage.getItem('firstEntryBenefits'))?.opticalBenefit || null,
        maternityBenefit: null,
        feminaPlusBenefit: null
      };
    },

    clearEntries() {
      if (confirm('Are you sure you want to clear all entries?')) {
        this.entries = [];
        localStorage.removeItem('entries');
        localStorage.removeItem('firstEntryBenefits');
      }
    },

    printQuote() {
        const doc = new jsPDF('p', 'mm', 'a4');
        const margin = 14;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const contentWidth = pageWidth - margin * 2;

        const currentDate = new Date();
        const timestamp = currentDate.toLocaleString();
        const day = String(currentDate.getDate()).padStart(2, '0');
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const year = currentDate.getFullYear();
        const get_date = `${day}/${month}/${year}`;
        const reference = generateReference('ICQ');

        const pageFooterHook = (data) => {
          addPageNumber(doc, data.pageNumber, pageHeight, pageWidth, margin);
        };

        let y = drawHeaderBanner(doc, {
          title: 'INDIVIDUAL HEALTH INSURANCE',
          logo: this.logo,
          pageWidth, margin,
          timestampLabel: `APA Individual Cover quotation tool - ${timestamp}`,
        });
        y = drawClientInfo(doc, this.clientDetails, y, margin);

        // Member benefits table
        y = drawSectionBar(doc, 'MEMBER BENEFITS', y, margin, contentWidth);
        autoTable(doc, {
            startY: y,
            head: [['Member Name', 'DOB', 'Age', 'Gender', 'Relationship', 'Inpatient Limit', 'Outpatient Limit', 'Dental Limit', 'Optical Limit', 'Maternity Limit', 'Femina Plus']],
            body: this.entries.map(entry => [
                entry.memberName,
                entry.dob,
                this.calculateAge(entry.dob),
                entry.gender,
                entry.relationship,
                this.formatAmount(entry.inpatientBenefit) || 'N/A',
                this.formatAmount(entry.outpatientBenefit) || 'N/A',
                this.formatAmount(entry.dentalBenefit) || 'N/A',
                this.formatAmount(entry.opticalBenefit) || 'N/A',
                this.formatAmount(entry.maternityBenefit) || 'N/A',
                entry.feminaPlusBenefit || '-'
            ]),
            margin: { left: margin, right: margin },
            theme: 'grid',
            headStyles: { fillColor: THEME.navy, textColor: 255, fontSize: 8.5, halign: 'center', valign: 'middle' },
            bodyStyles: { fontSize: 8, valign: 'middle', halign: 'center' },
            columnStyles: { 0: { halign: 'left' } },
            didDrawPage: pageFooterHook
        });
        y = doc.previousAutoTable.finalY + 8;

        // Premium breakdown table
        y = ensureSpace(doc, y, 20, pageHeight, margin);
        y = drawSectionBar(doc, 'PREMIUM BREAKDOWN', y, margin, contentWidth);
        autoTable(doc, {
            startY: y,
            head: [['Member Name', 'DOB', 'Age', 'Gender', 'Inpatient Premium', 'Outpatient Premium', 'Dental Premium', 'Optical Premium', 'Maternity Premium', 'Femina Premium', 'Total Premiums']],
            body: this.entries.map(entry => [
                entry.memberName,
                entry.dob,
                this.calculateAge(entry.dob),
                entry.gender,
                entry.inpatientBenefit ? this.formatAmount(this.calculatePremium('inpatient', entry.inpatientBenefit, entry.relationship, entry.age, entry.gender, entry)) : 'N/A',
                entry.outpatientBenefit ? this.formatAmount(this.calculatePremium('outpatient', entry.outpatientBenefit, entry.relationship, entry.age, entry.gender, entry)) : 'N/A',
                entry.dentalBenefit ? this.formatAmount(this.calculatePremium('dental', entry.dentalBenefit, entry.relationship, entry.age, entry.gender, entry)) : 'N/A',
                entry.opticalBenefit ? this.formatAmount(this.calculatePremium('optical', entry.opticalBenefit, entry.relationship, entry.age, entry.gender, entry)) : 'N/A',
                entry.maternityBenefit ? this.formatAmount(this.calculatePremium('maternity', entry.maternityBenefit, entry.relationship, entry.age, entry.gender, entry)) : 'N/A',
                entry.feminaPlusBenefit ? this.formatAmount(this.calculateFeminaPlusPremium(entry.feminaPlusBenefit)) : 'N/A',
                this.formatAmount(this.calculateTotalPremium(entry))
            ]),
            margin: { left: margin, right: margin },
            theme: 'grid',
            headStyles: { fillColor: THEME.navy, textColor: 255, fontSize: 8.5, halign: 'center', valign: 'middle' },
            bodyStyles: { fontSize: 8, valign: 'middle', halign: 'center' },
            columnStyles: { 0: { halign: 'left' } },
            didDrawPage: pageFooterHook
        });
        y = doc.previousAutoTable.finalY + 10;

        // Premium summary boxes
        y = ensureSpace(doc, y, 70, pageHeight, margin);
        y = drawSectionBar(doc, 'PREMIUM SUMMARY', y, margin, contentWidth);
        y = drawPremiumSummaryBoxes(doc, y, margin, contentWidth, {
          basicPremium: this.basicPremium,
          loadingPercentage: this.loadingPercentage,
          loadingAmount: this.loadingAmount,
          discountPercentage: this.discountPercentage,
          discountAmount: this.discountAmount,
          adjustedPremium: this.adjustedBasicPremium,
          levies: this.levies,
          stampDuty: this.stampDuty,
          totalDue: this.adjustedTotalPremium,
          totalMembers: this.entries.length,
          totalFamilies: 1,
          formatCurrency: this.formatCurrency,
        });

        // Terms & Conditions
        y = ensureSpace(doc, y, 20, pageHeight, margin);
        y = drawSectionBar(doc, 'TERMS & CONDITIONS', y, margin, contentWidth, { fill: THEME.red, gap: 3 });

        y = ensureSpace(doc, y, 15, pageHeight, margin);
        y = drawSectionBar(doc, 'WAITING PERIODS', y, margin, contentWidth, { height: 6, fontSize: 9 });
        y = drawBulletList(doc, [
          'No waiting period for accidents and accidental injuries',
          '30-day waiting period for acute illnesses',
          '3-month waiting period for newly-diagnosed chronic related conditions',
          '90-day waiting period for surgical cases not relating to pre-existing & chronic conditions',
          '10-month waiting period for maternity related conditions',
          '12-month waiting period for pre-existing and chronic related conditions',
          '12-month waiting period for congenital, prematurity & neonatal related conditions',
          '24-month waiting period for organ transplants excluding the cost of obtaining the organ, restricted to kidney, lung, heart, liver & bone marrow transplants',
          '12 months for Fibroid, Hernias, Adenoidectomy, tonsillectomy, and Haemorrhoids related treatment',
          'Any undeclared pre-existing conditions will lead to cancellation of the policy with no refund in premium.'
        ], margin + 2, y, contentWidth - 4, pageHeight, margin);
        y += 4;

        y = ensureSpace(doc, y, 15, pageHeight, margin);
        y = drawSectionBar(doc, 'DOCUMENTATION REQUIRED', y, margin, contentWidth, { height: 6, fontSize: 9 });
        y = drawBulletList(doc, [
          'Duly filled APA medical application form',
          "KYC (ID, KRA PIN, spouse's ID, Birth certificate/notification for the children)",
          'Any applicant who is 60 years and above must submit a current medical report on application for review',
          'Cover will commence after proof of payment is received.'
        ], margin + 2, y, contentWidth - 4, pageHeight, margin);
        y += 4;

        y = ensureSpace(doc, y, 15, pageHeight, margin);
        y = drawSectionBar(doc, 'PAYMENT OPTIONS', y, margin, contentWidth, { height: 6, fontSize: 9 });
        y = drawBulletList(doc, [
          'Bank transfer or Cheque deposits to APA medical bank account at NCBA Account No. 6447560039',
          'Insurance Premium Financing (IPF) – maximum of 4 instalments. IPF documents to be received on or before the renewal date of the policy period.',
          'Mpesa Paybill number 511600, Account: Name of the Principal member'
        ], margin + 2, y, contentWidth - 4, pageHeight, margin);
        y += 6;

        y = ensureSpace(doc, y, 10, pageHeight, margin);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('NB: This quote is valid for only 90 days from the date of issue.', margin, y);
        y += 10;

        y = ensureSpace(doc, y, 20, pageHeight, margin);
        drawFooterBlock(doc, y, margin, pageWidth, get_date, reference, 'Individual Cover Quote');

        // Save the document
        doc.save('quote.pdf');
        this.saveReport();
    },

    formatCurrency(value) {
        return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(value);
    },

    backHome() {
      this.entries = [];
      localStorage.removeItem('entries');
      this.$router.push('/');
    },

    saveReport() {
      const report = {
        clientName: this.clientDetails.name,
        email: this.clientDetails.email,
        phone: this.clientDetails.phone,
        numberOfEntries: this.entries.length,
        totalPremium: this.adjustedTotalPremium.toFixed(2),
        loadingPercentage: this.loadingPercentage,
        discountPercentage: this.discountPercentage
      };
      const reportJson = JSON.stringify(report);
      console.log('Report saved:', reportJson);
    },
  }
};
</script>

<style scoped>
/* Add any additional styling here */
</style>
